import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  SURECO_CSV_DATA_TYPE_OPTIONS,
  type SurecoCsvDataType,
} from '../constants'
import type {
  IdeonCompareCounts,
  IdeonCompareResult,
  IdeonCompareSummary,
  IdeonVersion,
} from '../api/ideon-compare-api'
import type { CompareRun } from '../api/compare-run-api'

dayjs.extend(relativeTime)

export type IdeonCompareSelection = {
  planYear: number | null
  baseVersionId: string | null
  targetVersionId: string | null
}

export function formatVersionDate(version: IdeonVersion): string {
  return dayjs(version.fetchedAt).format('MMM D, YYYY h:mm A')
}

/** "2 hours ago", falling back to a date for anything older than a week. */
export function formatCompletedAt(completedAt: string): string {
  const completed = dayjs(completedAt)
  return dayjs().diff(completed, 'day') > 7
    ? completed.format('MMM D, YYYY')
    : completed.fromNow()
}

export type IdeonVersionYearGroup = {
  planYear: number
  /** Newest fetch first. */
  versions: IdeonVersion[]
}

/** Versions grouped by plan year, newest year and newest fetch first. */
export function groupVersionsByYear(
  versions: IdeonVersion[]
): IdeonVersionYearGroup[] {
  const groups: IdeonVersionYearGroup[] = []
  ;[...versions]
    .sort((a, b) => b.fetchedAt.localeCompare(a.fetchedAt))
    .forEach((v) => {
      const group = groups.find((g) => g.planYear === v.planYear)
      if (group) group.versions.push(v)
      else groups.push({ planYear: v.planYear, versions: [v] })
    })
  return groups.sort((a, b) => b.planYear - a.planYear)
}

export function versionsForYear(
  groups: IdeonVersionYearGroup[],
  planYear: number | null
): IdeonVersion[] {
  return groups.find((g) => g.planYear === planYear)?.versions ?? []
}

/**
 * Fills in anything missing/invalid in the URL selection with defaults: the
 * newest plan year that has at least 2 fetches, and its 2 newest fetches
 * (older as base, newer as compare-to).
 */
export function resolveSelection(
  requested: {
    year?: string
    base?: string
    target?: string
  },
  versions: IdeonVersion[]
): IdeonCompareSelection {
  const groups = groupVersionsByYear(versions)

  const requestedYear = Number(requested.year)
  const defaultYear =
    groups.find((g) => g.versions.length >= 2)?.planYear ??
    groups[0]?.planYear ??
    null
  const planYear = groups.some((g) => g.planYear === requestedYear)
    ? requestedYear
    : defaultYear
  const yearVersions = versionsForYear(groups, planYear)
  const inYear = (id?: string) => yearVersions.some((v) => v.id === id)

  const baseVersionId = inYear(requested.base)
    ? (requested.base ?? null)
    : (yearVersions[1]?.id ?? null)
  const targetVersionId = inYear(requested.target)
    ? (requested.target ?? null)
    : (yearVersions.find((v) => v.id !== baseVersionId)?.id ?? null)

  return { planYear, baseVersionId, targetVersionId }
}

export function parseStatesParam(states?: string): string[] {
  return states ? states.split(',').filter(Boolean) : []
}

/**
 * Counts for one data type, summed over the selected states (empty = all).
 * Every run covers all states and stores per-state counts, so switching the
 * state filter never needs another request.
 */
export function countsFor(
  result: IdeonCompareResult,
  dataType: SurecoCsvDataType,
  states: string[]
): IdeonCompareCounts {
  const byState = result.summary.counts[dataType] ?? {}
  const selected = states.length > 0 ? states : Object.keys(byState)
  const counts = {
    addedCount: 0,
    removedCount: 0,
    modifiedCount: 0,
    unchangedCount: 0,
  }
  selected.forEach((state) => {
    const c = byState[state]
    if (!c) return
    counts.addedCount += c.added
    counts.removedCount += c.removed
    counts.modifiedCount += c.modified
    counts.unchangedCount += c.unchanged
  })
  return {
    ...counts,
    total:
      counts.addedCount +
      counts.removedCount +
      counts.modifiedCount +
      counts.unchangedCount,
  }
}

/**
 * Joins a completed run with the fetches it compared. Null when it isn't a
 * usable result (not finished yet, or a fetch is no longer listed).
 */
// The shared run endpoint types `summary` loosely (its shape depends on the
// run's source); this checks it really is an Ideon summary.
function isIdeonCompareSummary(value: unknown): value is IdeonCompareSummary {
  if (typeof value !== 'object' || value === null) return false
  if (!('states' in value) || !Array.isArray(value.states)) return false
  if (!('counts' in value)) return false
  const { counts } = value
  if (typeof counts !== 'object' || counts === null) return false
  if (!('warnings' in value)) return false
  const { warnings } = value
  return (
    typeof warnings === 'object' &&
    warnings !== null &&
    'parseErrors' in warnings &&
    typeof warnings.parseErrors === 'number' &&
    'skipped' in warnings &&
    Array.isArray(warnings.skipped)
  )
}

export function toIdeonCompareResult(
  run: CompareRun,
  versions: IdeonVersion[]
): IdeonCompareResult | null {
  const baseVersion = versions.find((v) => v.id === run.baseTaskId)
  const targetVersion = versions.find((v) => v.id === run.targetTaskId)
  if (
    run.source !== 'IDEON' ||
    run.status !== 'COMPLETED' ||
    run.year === null ||
    !isIdeonCompareSummary(run.summary) ||
    !baseVersion ||
    !targetVersion
  ) {
    return null
  }
  return {
    runId: run.runId,
    planYear: run.year,
    baseVersion,
    targetVersion,
    completedAt: run.completedAt ?? run.createdAt,
    summary: run.summary,
  }
}

/**
 * Plain-language note for rows of one data type that failed validation in
 * either fetch and were left out — or null when none were.
 */
export function skippedRowsMessage(
  result: IdeonCompareResult,
  dataType: SurecoCsvDataType,
  states: string[],
  fieldLabel: (key: string) => string
): string | null {
  const entries = result.summary.warnings.skipped.filter(
    (s) =>
      s.dataType === dataType &&
      (states.length === 0 || states.includes(s.state))
  )
  if (entries.length === 0) return null
  const plus = entries.some((s) => s.capped) ? '+' : ''
  const base = entries.reduce((sum, s) => sum + s.base, 0)
  const target = entries.reduce((sum, s) => sum + s.target, 0)
  const fields = Array.from(new Set(entries.flatMap((s) => s.fields)))
    .map(fieldLabel)
    .join(', ')
  const where = entries.map((s) => s.state).join(', ')
  return `${base.toLocaleString()}${plus} rows in the base fetch and ${target.toLocaleString()}${plus} in the compare-to fetch couldn't be read${fields ? ` (${fields})` : ''} in ${where}, so they were left out of this compare.`
}

export function countChanges(counts: IdeonCompareCounts): number {
  return counts.addedCount + counts.removedCount + counts.modifiedCount
}

/** First data type with any changes — the most useful tab to land on. */
export function defaultDataType(
  result: IdeonCompareResult | null,
  states: string[] = []
): SurecoCsvDataType {
  const withChanges = SURECO_CSV_DATA_TYPE_OPTIONS.find(
    ({ value }) => result && countChanges(countsFor(result, value, states)) > 0
  )
  return (withChanges ?? SURECO_CSV_DATA_TYPE_OPTIONS[0]).value
}

export function isSurecoCsvDataType(
  value: unknown
): value is SurecoCsvDataType {
  return SURECO_CSV_DATA_TYPE_OPTIONS.some((o) => o.value === value)
}
